import React from 'react';
import { useGetUsersVerificationStatusesByEmail } from 'hooks/useUserVerificationStatus';
import { useGetPage } from 'hooks/useGetPage';
import { pageIds } from 'utilities/constants';
import ActionField from './ActionField';
import { useNavigate } from 'react-router-dom';
import {
  LEGAL_DOCUMENTS_ROUTE,
  MY_PROFILE_VALIDATE_BAA,
  MY_PROFILE_VALIDATE_HIPPA,
  MY_PROFILE_VALIDATE_INSURANCE,
  MY_PROFILE_VALIDATE_PERSONA,
} from 'utilities/routes';
import {
  Hasura_Legal_Document_Types_Enum,
  Persona_Inquiry_Status_Enum,
} from 'graphql/generated/hasura';
import { PENDING_STATUSES } from 'const/persona-statuses';

export const ACTIONS_COUNT = 4;

const RequiredActions = () => {
  const { count, data } = useGetUsersVerificationStatusesByEmail();
  const { data: locale, loading: loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.PROVIDER_USER_PENDING_REVIEW,
  });
  const navigate = useNavigate();

  if (loading) {
    return null;
  }

  return (
    <div className="w-full mt-30 flex flex-col bg-white p-30 rounded-10 gap-5">
      <div>
        <p className="flex flex-col font-semibold desktop:flex-row-reverse gap-1 desktop:justify-between desktop:items-center">
          <p className="text-med-gray-3 text-base desktop:text-h5">
            {count}/{ACTIONS_COUNT} {locale?.completed}
          </p>
          <h3 className="desktop:text-h3 text-h4">{locale?.requiredActions}</h3>
        </p>
        <p className="text-neutral-800 font-medium">
          {locale?.requiredActionsSubtitle}
        </p>
      </div>

      <div className="flex flex-col gap-2.5">
        <ActionField
          title={locale?.IDVerification?.title}
          info={locale?.IDVerification?.info}
          buttonLabel={locale?.IDVerification?.button}
          error={locale?.IDVerification?.error}
          success={locale?.IDVerification?.success}
          tooltip={locale?.IDVerification?.tooltip}
          handleSubmit={() => navigate(MY_PROFILE_VALIDATE_PERSONA)}
          status={data.verifyId}
          successLabel={
            data.verifyId === Persona_Inquiry_Status_Enum.Approved
              ? locale?.verified
              : data.verifyId === Persona_Inquiry_Status_Enum.MarkedForReview
              ? 'Pending'
              : locale?.notVerified
          }
          pendingStatuses={[Persona_Inquiry_Status_Enum.MarkedForReview]}
          disabled={data.verifyId === Persona_Inquiry_Status_Enum.Approved}
        />
        {data.later ? (
          <ActionField
            title={locale?.insurance?.title}
            info={locale?.insurance?.info}
            buttonLabel={locale?.insurance?.button}
            error={locale?.insurance?.error}
            success={locale?.insurance?.success}
            tooltip={locale?.IDVerification?.tooltip}
            handleSubmit={() => ({})}
            status={Persona_Inquiry_Status_Enum.Approved}
            successLabel={
              data.insuranceId === Persona_Inquiry_Status_Enum.Approved
                ? locale?.verified
                : data.verifyId === Persona_Inquiry_Status_Enum.MarkedForReview
                ? 'Pending'
                : locale?.notVerified
            }
            buttonClassName="hidden"
            disabled={[
              Persona_Inquiry_Status_Enum.Approved,
              ...PENDING_STATUSES,
            ].includes(data.insuranceId as Persona_Inquiry_Status_Enum)}
          />
        ) : (
          <ActionField
            title={locale?.insurance?.title}
            info={locale?.insurance?.info}
            buttonLabel={locale?.insurance?.button}
            error={locale?.insurance?.error}
            success={locale?.insurance?.success}
            tooltip={locale?.IDVerification?.tooltip}
            handleSubmit={() => navigate(MY_PROFILE_VALIDATE_INSURANCE)}
            status={data?.insuranceId}
            successLabel={
              data.insuranceId === Persona_Inquiry_Status_Enum.Approved
                ? locale?.verified
                : locale?.notVerified
            }
            disabled={[
              Persona_Inquiry_Status_Enum.Approved,
              ...PENDING_STATUSES,
            ].includes(data.insuranceId as Persona_Inquiry_Status_Enum)}
          />
        )}

        <ActionField
          title={locale?.BAA?.title}
          info={locale?.BAA?.info}
          buttonLabel={locale?.BAA?.button}
          success={locale?.BAA?.success}
          handleSubmit={() => navigate(MY_PROFILE_VALIDATE_BAA)}
          status={data.baa?.status ?? null}
          successLabel={
            data.baa?.status === Persona_Inquiry_Status_Enum.Approved
              ? locale?.signed
              : locale?.notSigned ?? 'Not signed'
          }
          link={`${LEGAL_DOCUMENTS_ROUTE}/${Hasura_Legal_Document_Types_Enum.BaaAgreement}`}
          disabled={data.baa?.status === Persona_Inquiry_Status_Enum.Approved}
        />
        <ActionField
          title={locale?.HIPPA?.title}
          info={locale?.HIPPA?.info}
          buttonLabel={locale?.HIPPA?.button}
          success={locale?.HIPPA?.success}
          handleSubmit={() => navigate(MY_PROFILE_VALIDATE_HIPPA)}
          status={data.hippa?.status ?? null}
          successLabel={
            data.hippa?.status === Persona_Inquiry_Status_Enum.Approved
              ? locale?.signed
              : locale?.notSigned ?? 'Not signed'
          }
          link={`${LEGAL_DOCUMENTS_ROUTE}/${Hasura_Legal_Document_Types_Enum.HippaAgreement}`}
          disabled={data.hippa?.status === Persona_Inquiry_Status_Enum.Approved}
        />
      </div>
    </div>
  );
};

export default RequiredActions;
